import { useTableSort } from '@flexinet/ui-components';
import { DataTableSortStatus } from 'mantine-datatable';

import { useOrganizationAccountsQuery } from './useOrganizationAccountsQuery';
import { useOrganizationAccountsSearch } from './useOrganizationAccountsSearch';
import { OrganizationAccount } from '../types';

// DEFAULT: newest first, keyed on `created`.
//
// `created` is nullable on OrganizationAccount, which is not the ideal shape for
// a sort key — a nullable key degrades to insertion order if EVERY row lacks it.
// It is still the right key here: it is immutable (unlike `updated`, which would
// reorder the list under someone reading it), and the API's list resolver
// already queries `record-type-created-index` with `scanIndexForward: false`, so
// the first paint AGREES with the server instead of reshuffling it. Rows missing
// it sort last, which is correct.
//
// ⚠️ `created` must stay in the listOrganizationAccounts SELECTION SET for this
// to mean anything — it was absent until FNP-562 added it. Drop it from the
// query and this default silently becomes insertion order.
const DEFAULT_SORT_STATUS: DataTableSortStatus<OrganizationAccount> = {
    columnAccessor: 'created',
    direction: 'desc',
};

export const useOrganizationAccounts = (filters: Record<string, string> = {}) => {
    const query = useOrganizationAccountsQuery(filters);
    const { keyword } = useOrganizationAccountsSearch();

    // Type guard rather than a bare flatMap: a page can be undefined while a
    // fetch is in flight or after an error, and `undefined.items` throws.
    const items: OrganizationAccount[] = query.data?.pages?.flatMap(page =>
        (page && 'items' in page) ? page.items : []
    ) ?? [];

    // ⚠️ Both the keyword filter and the sort below cover only the pages
    // FETCHED SO FAR. Under token pagination the browser cannot filter or
    // reorder rows it has never seen, so the footer states a LOADED count
    // rather than a total, and `unfilteredCount` is reported alongside the
    // match count so the operator can see the search was scoped to a window.
    const needle = keyword.trim().toLowerCase();

    const matched = items.filter(item =>
        !needle ||
        item.accountId?.toLowerCase().includes(needle) ||
        item.account?.email?.toLowerCase().includes(needle)
    );

    // Sort state is component-level, not the module-scoped store: sort is how
    // ONE operator is viewing the table right now, unlike the keyword and the
    // filters, which are shared with sibling components.
    const {
        sortStatus,
        setSortStatus,
        sorted: visibleItems,
    } = useTableSort<OrganizationAccount>(matched, DEFAULT_SORT_STATUS);

    const fetchNextPage = () => {
        if (query.hasNextPage) {
            query.fetchNextPage();
        }
    };

    return {
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        isError: query.isError,
        isFetchingNextPage: query.isFetchingNextPage,
        unfilteredCount: items.length,
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchNextPage,
    };
};
